import { useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useUserState, { UserState } from "./useUserState";
import { confirmSetupIntent } from "../lib/helpers/setupIntentConfirmation";
import { PaymentInfo } from "../lib/types";
import PaymentInfoContext from "../context/PaymentInfoContext";

import {
  QUERY_PARAM_PLAN,
  QUERY_PARAM_REDIRECTION_STATUS,
  QUERY_PARAM_SETUP_INTENT,
  QUERY_PARAM_SETUP_INTENT_CLIENT_SECRET,
  REDIRECTION_STATUS_SUCCEEDED,
  ROUTE_FREE_TRIAL,
  ROUTE_FREE_TRIAL_B,
  ROUTE_SIGN_IN,
  ROUTE_SUBSCRIBE,
  ROUTE_WELCOME_ONBOARD,
  ROUTE_WELCOME_ONBOARD_SANITIZED,
} from "../lib/constants";

import { parseStripePlanId } from "../lib/plans";
import { SubscriptionData } from "../lib/mappers/subscriptionData";
import {
  urlMatchesSuccessfulPaymentIntent,
  urlMatchesSuccessfulSetupIntent,
} from "../lib/helpers/urlHelper";

const useRedirectDecider = () => {
  const { userState, subscriptionInfo } = useUserState();
  const paymentInfo = useContext<PaymentInfo>(PaymentInfoContext);
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionData = useMemo(() => {
    return new SubscriptionData(subscriptionInfo!, paymentInfo);
  }, [subscriptionInfo, paymentInfo]);

  useEffect(() => {
    const confirmAndRedirect = async (params: URLSearchParams) => {
      await confirmSetupIntent(
        params.get(QUERY_PARAM_SETUP_INTENT)!,
        params.get(QUERY_PARAM_SETUP_INTENT_CLIENT_SECRET)!,
        parseStripePlanId(params.get(QUERY_PARAM_PLAN))
      );
      navigate(ROUTE_WELCOME_ONBOARD_SANITIZED, { replace: true });
    };

    if (location.pathname === ROUTE_WELCOME_ONBOARD) {
      const params = new URLSearchParams(location.search);

      if (urlMatchesSuccessfulSetupIntent(params)) {
        confirmAndRedirect(params);
      }

      if (urlMatchesSuccessfulPaymentIntent(params)) {
        navigate(ROUTE_WELCOME_ONBOARD_SANITIZED, { replace: true });
      }
    }
  }, [location, navigate]);

  useEffect(() => {
    if (location.pathname === ROUTE_WELCOME_ONBOARD) {
      const params = new URLSearchParams(location.search);
      if (
        params.get(QUERY_PARAM_REDIRECTION_STATUS) ===
        REDIRECTION_STATUS_SUCCEEDED
      ) {
        // We stop redirection chain if query param matches condition
        return;
      }
    }

    if ([UserState.uninitialized, UserState.loading].includes(userState)) {
      return;
    }

    if (UserState.subscribed === userState) {
      if (location.pathname !== ROUTE_WELCOME_ONBOARD) {
        navigate(ROUTE_WELCOME_ONBOARD);
      }
    }

    if (userState === UserState.unauthenticated) {
      if (location.pathname !== ROUTE_SIGN_IN) {
        navigate(ROUTE_SIGN_IN);
        return;
      }
    }

    if (subscriptionData.missingPaymentInfo()) {
      return;
    }

    // The multiplan experiment only diverts trial-eligible users to the inline
    // 3-plan page. Already-trialed multiplan users fall through to the unchanged
    // control paths.
    if (
      subscriptionData.isMultiplan() &&
      userState === UserState.canRegisterForTrial
    ) {
      if (location.pathname !== ROUTE_FREE_TRIAL_B) {
        navigate(ROUTE_FREE_TRIAL_B);
      }
      return;
    }

    if (userState === UserState.canRegisterForTrial) {
      if (location.pathname !== ROUTE_FREE_TRIAL) {
        navigate(ROUTE_FREE_TRIAL);
        return;
      }
    }

    if (userState === UserState.paymentRequired) {
      if (location.pathname !== ROUTE_SUBSCRIBE) {
        navigate(ROUTE_SUBSCRIBE);
        return;
      }
    }
  }, [
    userState,
    location.pathname,
    navigate,
    location.search,
    location,
    paymentInfo,
    subscriptionData,
  ]);
};

export default useRedirectDecider;
